import { ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import Button from '@/components/common/Button';
import GenerateLinkButton from '@/components/common/GenerateLinkButton';
import PartyTabSection from '@/components/common/PartyTabSection';
import PlatformLink from '@/components/common/PlatformLink';
import cn from '@/lib/utils/cn';
import { toDateString } from '@/lib/utils/date';

import useRiskScan from '../../hooks/useRiskScan';

import AdvisoryReportTile from './AdvisoryReportTile';
import InventoryTile from './InventoryTile';

export default function RiskScanSection() {
  const { t } = useTranslation();

  const {
    latestScan,
    latestQuickScan,
    latestAdvisoryReport,
    downloadLatestAdvisoryPdf,
    advisoryReportPdfLink,
    handleSendQuickScan,
    handleSendRiskAnalysis,
  } = useRiskScan();

  return (
    <PartyTabSection
      icon={ShieldCheck}
      title={t('RISKSCAN_TITLE')}
      defaultCollapsed
    >
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-2">
          <InventoryTile />

          <AdvisoryReportTile />
        </div>

        <div className="flex flex-col gap-2 rounded-lg border p-2">
          <div className="text-sm">{t('LAST_ACTIVITY')}</div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs leading-tight">
                  {t('INVENTORY')}
                </span>

                <PlatformLink
                  to={
                    latestScan
                      ? `/conversations/risk-analysis/${latestScan?.id}`
                      : ''
                  }
                  target="_blank"
                  className={cn('text-sm leading-tight', {
                    ['underline']: Boolean(latestScan?.modified_at),
                  })}
                >
                  {toDateString(latestScan?.modified_at) ?? '-'}
                </PlatformLink>
              </div>

              <Button
                size="xs"
                variant="secondary"
                onClick={handleSendRiskAnalysis}
              >
                {t('MAIL_OPMAKEN')}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs leading-tight">
                  {t('QUICKSCAN')}
                </span>

                <PlatformLink
                  to={
                    latestQuickScan
                      ? `/conversations/risk-analysis/${latestQuickScan?.id}`
                      : ''
                  }
                  target="_blank"
                  className={cn('text-sm leading-tight', {
                    ['underline']: Boolean(latestQuickScan?.modified_at),
                  })}
                >
                  {toDateString(latestQuickScan?.modified_at) ?? '-'}
                </PlatformLink>
              </div>

              <Button
                size="xs"
                variant="secondary"
                onClick={handleSendQuickScan}
              >
                {t('MAIL_OPMAKEN')}
              </Button>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex flex-col">
                <span className="text-muted-foreground text-xs leading-tight">
                  {t('REPORT')}
                </span>

                <span className="text-sm leading-tight">
                  {toDateString(latestAdvisoryReport?.modified_at) ?? '-'}
                </span>
              </div>

              <GenerateLinkButton
                variant="secondary"
                size="xs"
                generate={downloadLatestAdvisoryPdf}
                generateLabel={t('GENERATE_REPORT')}
                link={advisoryReportPdfLink}
              >
                {t('VIEW_REPORT')}
              </GenerateLinkButton>
            </div>
          </div>
        </div>
      </div>
    </PartyTabSection>
  );
}
